<script>
import axios from 'axios'
import TopsItemDetail from './tops_item_detail'
import {
  formatAge,
  formatBytes,
  formatCount,
  formatDate,
  formatMetric,
  formatMetricCompact,
  riskVariant,
  windowLabel
} from './tops_formatters'

const REFRESH_INTERVAL_MS = 60000
// The collector runs hourly; a newest snapshot older than this means the
// numbers on screen are no longer the current hour's.
const STALE_SNAPSHOT_MINUTES = 150
const OVERVIEW_LIMIT = 10
const SEARCH_DEBOUNCE_MS = 350
const BAR_COLORS = ['#727cf5', '#0acf97', '#fa5c7c', '#ffbc00', '#39afd1']

export default {
  components: {
    TopsItemDetail
  },
  props: {
    basePath: {
      type: String,
      default: '/monitoring/tops'
    }
  },
  data () {
    return {
      // eslint-disable-next-line
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      catalog: { reports: [], sites: [], devices: [], window_options: [] },
      catalogLoaded: false,
      catalogError: '',
      // False until the filters were restored from the query string: the
      // watchers must not react to that restore (it would reset the member
      // filter and fire duplicate requests).
      ready: false,
      // Filters. They mirror the query string so a view can be shared.
      report: '',
      site: '',
      device: '',
      hours: 24,
      metric: '',
      limit: 25,
      q: '',
      searchText: '',
      searchTimer: null,
      limitOptions: [10, 25, 50, 100],
      summary: {},
      summaryLoaded: false,
      summaryError: '',
      ranking: {},
      rankingLoaded: false,
      rankingError: '',
      rankingBusy: false,
      overview: {},
      overviewLoaded: false,
      overviewError: '',
      coverage: {},
      coverageLoaded: false,
      coverageError: '',
      snapshotsOpen: false,
      snapshots: [],
      snapshotsTotal: 0,
      snapshotsPage: 1,
      snapshotsPerPage: 25,
      snapshotsWithRows: true,
      snapshotsBusy: false,
      snapshotsError: '',
      snapshotDoc: null,
      snapshotDocError: '',
      selectedItem: null,
      isBusy: false,
      loadError: '',
      exporting: '',
      lastUpdated: null,
      refreshTimer: null
    }
  },
  computed: {
    baseURL () {
      return this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
    },
    base () {
      return `${this.baseURL}${this.basePath}`
    },
    reportsWithData () {
      return this.catalog.reports.filter((entry) => entry.has_data && entry.preset)
    },
    reportsWithoutData () {
      return this.catalog.reports.filter((entry) => !entry.has_data)
    },
    currentReport () {
      return this.catalog.reports.find((entry) => entry.report === this.report) || null
    },
    preset () {
      return (this.currentReport && this.currentReport.preset) || null
    },
    reportOptions () {
      const options = [{ value: '', text: 'All reports (overview)' }]
      this.catalog.reports.forEach((entry) => {
        options.push({
          value: entry.report,
          text: entry.has_data ? entry.label : `${entry.label} — no data yet`,
          disabled: !entry.has_data || !entry.preset
        })
      })
      return options
    },
    siteOptions () {
      const options = [{ value: '', text: 'All sites' }]
      this.catalog.sites.forEach((entry) => {
        options.push({ value: entry.site, text: entry.site })
      })
      return options
    },
    siteEntry () {
      return this.catalog.sites.find((entry) => entry.site === this.site) || null
    },
    deviceOptions () {
      if (!this.siteEntry || this.siteEntry.devices.length < 2) return []
      return [{ value: '', text: 'Both HA members' }].concat(
        this.siteEntry.devices.map((name) => ({ value: name, text: name })))
    },
    windowOptions () {
      const hours = this.catalog.window_options.length
        ? this.catalog.window_options
        : [1, 6, 24, 72, 168, 720]
      return hours.map((value) => ({ value, text: windowLabel(value) }))
    },
    metricOptions () {
      if (!this.preset) return []
      return this.preset.metrics.map((entry) => ({ value: entry.field, text: entry.label }))
    },
    activeMetric () {
      if (!this.preset) return ''
      const fields = this.preset.metrics.map((entry) => entry.field)
      return fields.includes(this.metric) ? this.metric : this.preset.default_metric
    },
    activeMetricInfo () {
      if (!this.preset) return {}
      return this.preset.metrics.find((entry) => entry.field === this.activeMetric) || {}
    },
    scopeLabel () {
      if (this.device) return this.device
      if (this.site) return `${this.site}${this.siteEntry && this.siteEntry.ha_pair ? ' (HA pair)' : ''}`
      return 'All sites'
    },
    rankingItems () {
      return this.ranking.items || []
    },
    rankingChartItems () {
      return this.rankingItems.slice(0, Math.min(this.limit, 25))
    },
    rankingSeries () {
      return [{
        name: this.activeMetricInfo.label || 'Value',
        data: this.rankingChartItems.map((item) => item[this.activeMetric] || 0)
      }]
    },
    rankingOptions () {
      return this.barOptions(
        this.rankingChartItems.map((item) => this.itemLabel(item, this.preset)),
        this.activeMetricInfo.unit,
        BAR_COLORS[0])
    },
    tableFields () {
      if (!this.preset) return []
      const fields = [
        { key: 'position', label: '#', sortable: true, thStyle: { width: '48px' } },
        { key: 'key', label: this.preset.key_label || 'Item', sortable: true }
      ]
      this.preset.group_by.forEach((entry) => {
        fields.push({ key: entry.field, label: entry.label, sortable: true })
      })
      this.preset.metrics.forEach((entry) => {
        fields.push({
          key: entry.field,
          label: entry.label,
          sortable: true,
          class: 'text-right text-nowrap',
          formatter: (value) => formatMetric(value, entry.unit)
        })
      })
      fields.push(
        { key: 'share_pct', label: 'Share', sortable: true, class: 'text-right' },
        { key: 'runs_present', label: 'Hours in top', sortable: true, class: 'text-right text-nowrap' },
        { key: 'best_rank', label: 'Best rank', sortable: true, class: 'text-right' },
        { key: 'last_seen', label: 'Last seen', sortable: true, class: 'text-nowrap', formatter: (value) => formatDate(value) }
      )
      return fields
    },
    // How many of the hourly top-N snapshots in the window each ranked item
    // appeared in. Below 100% the sum is a lower bound: in the missing hours
    // the item was outside that hour's top 25 and is not counted. An item
    // missing one hour in a week is still a solid total, hence the 90% bar.
    completeness () {
      const runs = this.ranking.snapshot_runs || 0
      if (!runs || !this.rankingItems.length) return null
      const solid = this.rankingItems.filter((item) => item.runs_present >= runs * 0.9).length
      return { runs, solid, total: this.rankingItems.length }
    },
    overviewCards () {
      return this.reportsWithData.map((entry, index) => {
        const result = this.overview[entry.report] || {}
        const preset = entry.preset
        const metric = preset.default_metric
        const info = preset.metrics.find((m) => m.field === metric) || {}
        const items = result.items || []
        return {
          entry,
          result,
          items,
          metricLabel: info.label,
          total: (result.total || {})[metric],
          unit: info.unit,
          series: [{ name: info.label, data: items.map((item) => item[metric] || 0) }],
          options: this.barOptions(
            items.map((item) => this.itemLabel(item, preset)),
            info.unit,
            BAR_COLORS[index % BAR_COLORS.length])
        }
      })
    },
    collector () {
      return this.summary.collector || {}
    },
    lastRun () {
      return this.collector.last_run || {}
    },
    snapshotStale () {
      const age = this.summary.newest_age_seconds
      return typeof age === 'number' && age > STALE_SNAPSHOT_MINUTES * 60
    },
    // Classified by the backend from failures per possible attempt (runs x
    // devices): "persistent" kinds fail almost every time and are a
    // collector configuration problem, "intermittent" ones are poll noise.
    failingReports () {
      return this.summary.failing_reports || []
    },
    persistentFailures () {
      return this.failingReports.filter((entry) => entry.status === 'persistent')
    },
    intermittentFailures () {
      return this.failingReports.filter((entry) => entry.status !== 'persistent')
    },
    coverageReports () {
      return this.coverage.reports || []
    },
    snapshotFields () {
      return [
        { key: 'ts', label: 'Collected', formatter: (value) => formatDate(value), class: 'text-nowrap' },
        { key: 'device', label: 'Device' },
        { key: 'label', label: 'Report' },
        { key: 'report_window', label: 'Report window (firewall clock)', class: 'text-nowrap' },
        { key: 'row_count', label: 'Rows', class: 'text-right' },
        { key: 'duration_ms', label: 'Took', class: 'text-right', formatter: (value) => typeof value === 'number' ? `${(value / 1000).toFixed(1)} s` : '—' }
      ]
    }
  },
  watch: {
    report () {
      if (!this.ready) return
      // A metric picked for another report kind does not exist on this one.
      this.metric = ''
      this.syncQuery()
      this.loadMain()
      this.reloadSnapshots()
    },
    site () {
      if (!this.ready) return
      // Keep the member only when it belongs to the new site.
      if (this.device && !(this.siteEntry && this.siteEntry.devices.includes(this.device))) this.device = ''
      this.syncQuery()
      this.loadMain()
      this.reloadSnapshots()
    },
    device () {
      if (!this.ready) return
      this.syncQuery()
      this.loadMain()
      this.reloadSnapshots()
    },
    hours () {
      if (!this.ready) return
      this.syncQuery()
      this.loadAll()
    },
    metric () {
      if (!this.ready) return
      this.syncQuery()
      this.loadMain()
    },
    limit () {
      if (!this.ready) return
      this.syncQuery()
      this.loadMain()
    },
    searchText (value) {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.q = value.trim()
      }, SEARCH_DEBOUNCE_MS)
    },
    q () {
      if (!this.ready) return
      this.syncQuery()
      this.loadMain()
    },
    snapshotsPage () {
      this.loadSnapshots()
    },
    snapshotsWithRows () {
      this.reloadSnapshots()
    }
  },
  mounted () {
    this.init()
    // Do not poll from background tabs (see sdwan.vue for rationale).
    this.refreshTimer = setInterval(() => {
      if (document.hidden) return
      this.loadAll()
    }, REFRESH_INTERVAL_MS)
  },
  beforeDestroy () {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    clearTimeout(this.searchTimer)
  },
  methods: {
    formatAge,
    formatBytes,
    formatCount,
    formatDate,
    formatMetric,
    riskVariant,
    windowLabel,
    parse (data) {
      return typeof data === 'string' ? JSON.parse(data) : data
    },
    // The filters are restored only once the catalog is known, so a shared
    // link naming a report or site that no longer exists falls back to the
    // overview instead of an error.
    init () {
      return this.loadCatalog().then(() => {
        if (!this.catalogLoaded) return
        this.readQuery()
        // Runs after the watcher flush that the restore queued.
        this.$nextTick(() => {
          this.ready = true
          this.loadAll()
        })
      })
    },
    refresh () {
      if (!this.catalogLoaded) this.init()
      else this.loadAll()
    },
    // --- query string -----------------------------------------------------
    readQuery () {
      const query = this.$route.query || {}
      const report = query.report || ''
      this.report = this.reportsWithData.some((entry) => entry.report === report) ? report : ''
      const site = this.catalog.sites.find((entry) => entry.site === query.site)
      this.site = site ? site.site : ''
      this.device = site && site.devices.includes(query.device) ? query.device : ''
      const hours = parseInt(query.hours, 10)
      this.hours = Number.isInteger(hours) && hours > 0 ? hours : 24
      this.metric = query.metric || ''
      const limit = parseInt(query.limit, 10)
      this.limit = this.limitOptions.includes(limit) ? limit : 25
      this.q = query.q || ''
      this.searchText = this.q
    },
    syncQuery () {
      const query = {}
      if (this.report) query.report = this.report
      if (this.site) query.site = this.site
      if (this.device) query.device = this.device
      if (this.hours !== 24) query.hours = String(this.hours)
      if (this.metric) query.metric = this.metric
      if (this.limit !== 25) query.limit = String(this.limit)
      if (this.q) query.q = this.q
      const current = JSON.stringify(this.$route.query || {})
      if (current === JSON.stringify(query)) return
      // Replace rather than push: every filter tweak is not a history entry.
      // Callback form: this vue-router returns no promise from replace(), and
      // the no-op abort callback swallows the duplicate-navigation case.
      this.$router.replace({ query }, () => {}, () => {})
    },
    scopeParams (extra) {
      const params = Object.assign({ hours: this.hours }, extra || {})
      if (this.device) params.device = this.device
      else if (this.site) params.site = this.site
      return params
    },
    toQuery (params) {
      return Object.keys(params)
        .filter((key) => params[key] !== '' && params[key] !== null && params[key] !== undefined)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')
    },
    // --- loading ----------------------------------------------------------
    loadCatalog () {
      return axios.get(`${this.base}/catalog`)
        .then((response) => {
          this.catalog = this.parse(response.data)
          this.catalogLoaded = true
          this.catalogError = ''
        })
        .catch((error) => {
          this.catalogError = error.message
          this.loadError = `Could not load the report catalog: ${error.message}`
        })
    },
    loadAll () {
      // Never stack a refresh cycle on top of one still in flight.
      if (this.isBusy || !this.catalogLoaded) return
      this.isBusy = true
      this.loadError = ''
      const requests = [this.loadSummary(), this.loadCoverage(), this.loadMain()]
      if (this.snapshotsOpen) requests.push(this.loadSnapshots())
      Promise.all(requests).finally(() => {
        this.isBusy = false
        this.lastUpdated = new Date()
      })
    },
    loadSummary () {
      return axios.get(`${this.base}/summary?${this.toQuery({ hours: this.hours })}`)
        .then((response) => {
          this.summary = this.parse(response.data)
          this.summaryError = ''
          this.summaryLoaded = true
        })
        .catch((error) => {
          this.summaryError = error.message
        })
    },
    loadCoverage () {
      return axios.get(`${this.base}/coverage?${this.toQuery({ hours: this.hours })}`)
        .then((response) => {
          this.coverage = this.parse(response.data)
          this.coverageError = ''
          this.coverageLoaded = true
        })
        .catch((error) => {
          this.coverageError = error.message
        })
    },
    loadMain () {
      if (!this.catalogLoaded) return Promise.resolve()
      return this.report ? this.loadRanking() : this.loadOverview()
    },
    loadRanking () {
      const report = this.report
      const params = this.scopeParams({ report, metric: this.activeMetric, limit: this.limit, q: this.q })
      this.rankingBusy = true
      return axios.get(`${this.base}/ranking?${this.toQuery(params)}`)
        .then((response) => {
          // A slower answer for a report the user already moved away from
          // must not overwrite the current one.
          if (report !== this.report) return
          const data = this.parse(response.data)
          this.ranking = data
          this.rankingError = data.error || ''
          this.rankingLoaded = true
        })
        .catch((error) => {
          if (report === this.report) this.rankingError = `Could not load this ranking: ${error.message}`
        })
        .finally(() => {
          this.rankingBusy = false
        })
    },
    loadOverview () {
      const scope = this.scopeParams()
      const requests = this.reportsWithData.map((entry) => {
        const params = Object.assign({ report: entry.report, limit: OVERVIEW_LIMIT }, scope)
        return axios.get(`${this.base}/ranking?${this.toQuery(params)}`)
          .then((response) => [entry.report, this.parse(response.data)])
      })
      return Promise.all(requests)
        .then((results) => {
          const overview = {}
          results.forEach(([report, data]) => { overview[report] = data })
          this.overview = overview
          this.overviewError = ''
          this.overviewLoaded = true
        })
        .catch((error) => {
          this.overviewError = `Could not load the overview: ${error.message}`
        })
    },
    reloadSnapshots () {
      if (!this.snapshotsOpen) return
      if (this.snapshotsPage !== 1) {
        this.snapshotsPage = 1 // the watcher reloads
        return
      }
      this.loadSnapshots()
    },
    loadSnapshots () {
      const params = this.scopeParams({
        report: this.report,
        page: this.snapshotsPage,
        page_size: this.snapshotsPerPage,
        with_rows: this.snapshotsWithRows ? 'yes' : ''
      })
      this.snapshotsBusy = true
      return axios.get(`${this.base}/snapshots?${this.toQuery(params)}`)
        .then((response) => {
          const data = this.parse(response.data)
          this.snapshots = (data.items || []).map((item) => Object.assign({}, item, {
            report_window: item.report_start && item.report_end
              ? `${item.report_start} – ${item.report_end.slice(11)}`
              : '—'
          }))
          this.snapshotsTotal = data.total_count || 0
          this.snapshotsError = data.error || ''
        })
        .catch((error) => {
          this.snapshotsError = `Could not load snapshots: ${error.message}`
        })
        .finally(() => {
          this.snapshotsBusy = false
        })
    },
    toggleSnapshots () {
      this.snapshotsOpen = !this.snapshotsOpen
      if (this.snapshotsOpen) this.loadSnapshots()
    },
    openSnapshot (row) {
      this.snapshotDoc = null
      this.snapshotDocError = ''
      this.$refs['modal-snapshot'].show()
      axios.get(`${this.base}/snapshot/${encodeURIComponent(row.id)}`)
        .then((response) => {
          const data = this.parse(response.data)
          if (data.error) this.snapshotDocError = data.error
          else this.snapshotDoc = data
        })
        .catch((error) => {
          this.snapshotDocError = `Could not load this snapshot: ${error.message}`
        })
    },
    snapshotRowFields (doc) {
      const rows = (doc && doc.rows) || []
      if (!rows.length) return []
      return Object.keys(rows[0]).map((key) => ({ key, label: key }))
    },
    downloadCsv () {
      const params = this.scopeParams({ report: this.report, metric: this.activeMetric, q: this.q })
      const filename = `ngfw_${this.report.replace(/-/g, '_')}.csv`
      this.exporting = filename
      axios
        .get(`${this.base}/ranking/csv?${this.toQuery(params)}`, { responseType: 'blob' })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          this.loadError = `Could not export ${filename}: ${error.message}`
        })
        .finally(() => {
          this.exporting = ''
        })
    },
    // --- presentation -----------------------------------------------------
    itemLabel (item, preset) {
      if (!preset || !preset.group_by.length) return item.key
      const extra = preset.group_by.map((entry) => item[entry.field]).filter(Boolean)
      return extra.length ? `${item.key} (${extra.join(', ')})` : item.key
    },
    barOptions (categories, unit, color) {
      return {
        chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false } },
        // Labels sit past the end of each bar: inside, the short bars of a
        // long-tailed ranking cannot hold them.
        plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 3, dataLabels: { position: 'top', hideOverflowingLabels: false } } },
        dataLabels: {
          enabled: true,
          formatter: (value) => formatMetricCompact(value, unit),
          offsetX: 28,
          style: { fontSize: '11px' }
        },
        colors: [color],
        xaxis: {
          categories,
          labels: { formatter: (value) => formatMetricCompact(Number(value), unit) }
        },
        yaxis: { labels: { maxWidth: 220 } },
        tooltip: { y: { formatter: (value) => formatMetric(value, unit) } },
        grid: { padding: { right: 36 } }
      }
    },
    chartHeight (count) {
      return Math.max(200, count * 28)
    },
    openReport (report) {
      this.report = report
    },
    showItem (item) {
      this.selectedItem = item
      this.$refs['modal-item'].show()
    },
    onModalShown () {
      // ApexCharts sizes itself against the width available when it renders;
      // inside a modal that width is only final once the dialog is shown.
      this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    },
    coverageCell (row, report) {
      return (row.cells || {})[report] || null
    },
    coverageTitle (row, report) {
      const cell = this.coverageCell(row, report)
      if (!cell) return 'No snapshot collected in this window'
      return Object.keys(cell.by_device).sort()
        .map((name) => `${name}: ${cell.by_device[name].with_rows}/${cell.by_device[name].docs} with rows`)
        .join('\n')
    },
    coverageClass (row, report) {
      const cell = this.coverageCell(row, report)
      if (!cell || !cell.docs) return 'text-muted'
      if (!cell.with_rows) return 'text-muted coverage-empty'
      // Only one member of a pair forwards traffic at a time, so a healthy
      // site returns rows in about one snapshot per run, not one per device.
      const expected = cell.docs / Math.max(1, (row.devices || []).length)
      return cell.with_rows >= expected * 0.9 ? 'text-success' : 'text-warning'
    }
  }
}
</script>

<template>
  <div class="tops-page">
    <b-alert
      :show="loadError.length > 0"
      variant="danger"
      dismissible
      @dismissed="loadError = ''"
    >
      {{ loadError }}
    </b-alert>

    <b-alert
      :show="collector.status === 'down' || snapshotStale"
      variant="warning"
      class="d-flex align-items-center py-1"
    >
      <feather
        type="alert-triangle"
        class="icon-xs mr-2"
      />
      <span v-if="collector.status === 'down'">
        <b>Collector down</b> — the last top-reports run was {{ formatAge(collector.age_seconds) }}
        (expected hourly). The rankings below stop at that run.
      </span>
      <span v-else>
        <b>No fresh data</b> — the newest top-report snapshot is
        {{ formatAge(summary.newest_age_seconds) }}; the collector is running but no firewall returned a report.
      </span>
    </b-alert>

    <!-- Toolbar -->
    <div class="d-flex align-items-center mb-2">
      <span
        v-if="lastUpdated"
        class="text-muted font-size-12"
      >
        Updated {{ formatDate(lastUpdated) }} (America/Bogota) · auto-refresh 60 s
      </span>
      <b-button
        size="sm"
        variant="outline-primary"
        class="ml-auto"
        :disabled="isBusy"
        @click="refresh"
      >
        <feather
          type="refresh-cw"
          class="icon-xs mr-1"
        />
        Refresh
      </b-button>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body py-2">
        <div class="row tops-filters">
          <div class="col-lg-3 col-md-6 col-12 mb-2">
            <label class="font-size-12 text-muted mb-0">Report</label>
            <b-form-select
              v-model="report"
              size="sm"
              :options="reportOptions"
            />
          </div>
          <div class="col-lg-2 col-md-6 col-12 mb-2">
            <label class="font-size-12 text-muted mb-0">
              Site
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="icon-xs help-tip"
                title="A site is an HA pair (FW01 and FW02 of the same name). Its ranking adds both members, because only the active member forwards traffic and the passive one reports nothing."
              />
            </label>
            <b-form-select
              v-model="site"
              size="sm"
              :options="siteOptions"
            />
          </div>
          <div
            v-if="deviceOptions.length"
            class="col-lg-2 col-md-6 col-12 mb-2"
          >
            <label class="font-size-12 text-muted mb-0">Member</label>
            <b-form-select
              v-model="device"
              size="sm"
              :options="deviceOptions"
            />
          </div>
          <div class="col-lg-2 col-md-6 col-12 mb-2">
            <label class="font-size-12 text-muted mb-0">Window</label>
            <b-form-select
              v-model="hours"
              size="sm"
              :options="windowOptions"
            />
          </div>
          <template v-if="report">
            <div class="col-lg-1 col-md-4 col-6 mb-2">
              <label class="font-size-12 text-muted mb-0">Rank by</label>
              <b-form-select
                v-model="metric"
                size="sm"
                :options="[{ value: '', text: 'Default' }].concat(metricOptions)"
              />
            </div>
            <div class="col-lg-1 col-md-2 col-6 mb-2">
              <label class="font-size-12 text-muted mb-0">Top</label>
              <b-form-select
                v-model="limit"
                size="sm"
                :options="limitOptions"
              />
            </div>
            <div class="col-lg col-md-6 col-12 mb-2">
              <label class="font-size-12 text-muted mb-0">Search</label>
              <b-form-input
                v-model="searchText"
                size="sm"
                type="search"
                :placeholder="`Filter ${(preset && preset.key_label || 'item').toLowerCase()}...`"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Nothing below can be drawn without the catalog (report kinds,
         presets, sites); without it the panels would show zeros as data. -->
    <div
      v-if="!catalogLoaded && catalogError"
      class="text-danger py-4 text-center font-size-13"
    >
      Could not load the top reports ({{ catalogError }}). Use Refresh to try again.
    </div>
    <div
      v-else-if="!catalogLoaded"
      class="text-muted py-4 text-center"
    >
      <b-spinner small />&nbsp;&nbsp;Loading top reports...
    </div>
    <template v-else>
      <!-- KPI and collector health -->
      <div
        v-if="!summaryLoaded && summaryError"
        class="text-danger py-3 text-center font-size-13"
      >
        Could not load the summary: {{ summaryError }}
      </div>
      <div
        v-else-if="!summaryLoaded"
        class="text-muted py-3 text-center"
      >
        <b-spinner small />&nbsp;&nbsp;Loading summary...
      </div>
      <div
        v-else
        class="row"
      >
        <div class="col-xl-3 col-md-6 col-12 mb-3">
          <div class="card h-100 mb-0">
            <div class="card-body py-2">
              <h5 class="text-muted font-size-13 mb-1">
                Snapshots with data
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  title="Hourly report documents in the window that returned at least one row, out of all collected. The passive member of each HA pair always answers with an empty report."
                />
              </h5>
              <h3 class="mb-0">
                {{ formatCount(summary.snapshots_with_rows || 0) }}<small class="text-muted font-size-13">/{{ formatCount(summary.snapshots || 0) }}</small>
              </h3>
              <div class="text-muted font-size-12">
                {{ summary.runs || 0 }} hourly runs · {{ windowLabel(hours).toLowerCase() }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 col-12 mb-3">
          <div class="card h-100 mb-0">
            <div class="card-body py-2">
              <h5 class="text-muted font-size-13 mb-1">
                Sites reporting
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  title="Sites with at least one member that returned rows in the window, out of the sites seen in the last 30 days."
                />
              </h5>
              <h3 class="mb-0">
                {{ summary.sites_with_rows || 0 }}<small class="text-muted font-size-13">/{{ summary.sites_known || 0 }}</small>
              </h3>
              <div
                class="text-muted font-size-12 text-truncate"
                :title="(summary.devices_with_rows || []).join(', ')"
              >
                Active members: {{ (summary.devices_with_rows || []).join(', ') || '—' }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 col-12 mb-3">
          <div class="card h-100 mb-0">
            <div class="card-body py-2">
              <h5 class="text-muted font-size-13 mb-1">
                Collector
              </h5>
              <h3 class="mb-0">
                <b-badge
                  :variant="collector.status === 'ok' ? 'success' : collector.status === 'down' ? 'danger' : 'secondary'"
                  class="font-size-13 align-middle"
                >
                  {{ collector.status || 'unknown' }}
                </b-badge>
                <small class="text-muted font-size-13">last run {{ formatAge(collector.age_seconds) }}</small>
              </h3>
              <div
                v-if="lastRun.reports"
                class="text-muted font-size-12"
              >
                Last run: {{ formatCount(lastRun.reports_ok) }} reports ok ·
                {{ formatCount(lastRun.reports_empty) }} empty ·
                {{ formatCount(lastRun.reports_failed) }} failed ({{ lastRun.devices_total }} devices polled)
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 col-12 mb-3">
          <div class="card h-100 mb-0">
            <div class="card-body py-2">
              <h5 class="text-muted font-size-13 mb-1">
                Report kinds always failing
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  title="Kinds that failed in at least 90% of the attempts (hourly runs x firewalls) in the window. These never produce data and point at the collector configuration, not at the firewalls. The collector counts a device as failed when any single kind fails, so its own device totals overstate the problem."
                />
              </h5>
              <h3 class="mb-0">
                <span :class="{ 'text-warning': persistentFailures.length > 0 }">{{ persistentFailures.length }}</span>
                <small class="text-muted font-size-13">of {{ catalog.reports.length }} kinds</small>
              </h3>
              <div class="text-muted font-size-12">
                {{ intermittentFailures.length }} more with occasional errors ·
                <a
                  v-if="failingReports.length"
                  id="failing-reports"
                  href="javascript:void(0)"
                >details</a>
              </div>
              <b-popover
                v-if="failingReports.length"
                target="failing-reports"
                triggers="click blur"
                placement="bottom"
                title="Collector errors by report kind"
              >
                <div
                  v-for="entry in failingReports"
                  :key="entry.report"
                  class="font-size-12 mb-2"
                >
                  <b-badge
                    :variant="entry.status === 'persistent' ? 'warning' : 'light'"
                    class="mr-1"
                  >
                    {{ entry.failure_pct }}%
                  </b-badge>
                  <b>{{ entry.label }}</b> — {{ entry.types.join(', ') }} on
                  {{ entry.devices.length }} device{{ entry.devices.length === 1 ? '' : 's' }}
                  <div
                    v-if="entry.sample_error"
                    class="text-muted font-size-11 text-truncate"
                    :title="entry.sample_error"
                  >
                    {{ entry.sample_error }}
                  </div>
                </div>
              </b-popover>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="summaryLoaded && summaryError"
        class="text-danger font-size-13 mb-2"
      >
        The last summary refresh failed ({{ summaryError }}); the figures above are from the previous one.
      </div>

      <!-- Overview: every report kind side by side -->
      <template v-if="!report">
        <div
          v-if="overviewError"
          class="text-danger py-3 text-center font-size-13"
        >
          {{ overviewError }}
        </div>
        <div
          v-else-if="!overviewLoaded"
          class="text-muted py-4 text-center"
        >
          <b-spinner small />&nbsp;&nbsp;Loading top reports...
        </div>
        <div
          v-else-if="!overviewCards.length"
          class="text-muted py-4 text-center"
        >
          No report kind has returned rows yet.
        </div>
        <div
          v-else
          class="row"
        >
          <div
            v-for="card in overviewCards"
            :key="card.entry.report"
            class="col-xl-4 col-lg-6 col-12 mb-3"
          >
            <div class="card h-100 mb-0">
              <div class="card-body py-2">
                <div class="d-flex align-items-center mb-1">
                  <h5 class="font-size-14 mb-0 text-truncate">
                    <feather
                      :type="card.entry.preset.icon || 'list'"
                      class="icon-xs mr-1 text-muted"
                    />
                    {{ card.entry.label }}
                  </h5>
                  <b-button
                    size="sm"
                    variant="link"
                    class="ml-auto py-0"
                    @click="openReport(card.entry.report)"
                  >
                    View all
                  </b-button>
                </div>
                <div class="text-muted font-size-12 mb-1">
                  Top {{ card.items.length }} by {{ (card.metricLabel || '').toLowerCase() }} · {{ scopeLabel }} ·
                  total {{ formatMetric(card.total, card.unit) }}
                </div>
                <apexchart
                  v-if="card.items.length"
                  type="bar"
                  :height="chartHeight(card.items.length)"
                  :series="card.series"
                  :options="card.options"
                />
                <div
                  v-else-if="card.result.error"
                  class="text-muted py-4 text-center font-size-13"
                >
                  {{ card.result.error }}
                </div>
                <div
                  v-else
                  class="text-muted py-4 text-center font-size-13"
                >
                  No rows for {{ scopeLabel }} in this window.
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- One report kind: chart and full table -->
      <template v-else>
        <div class="row">
          <div class="col-xl-5 col-12 mb-3">
            <div class="card h-100 mb-0">
              <div class="card-body py-2">
                <h5 class="font-size-14 mb-1">
                  <feather
                    :type="(preset && preset.icon) || 'list'"
                    class="icon-xs mr-1 text-muted"
                  />
                  {{ currentReport && currentReport.label }} by {{ (activeMetricInfo.label || '').toLowerCase() }}
                </h5>
                <div class="text-muted font-size-12 mb-1">
                  {{ scopeLabel }} · {{ windowLabel(hours).toLowerCase() }} · summed over
                  {{ ranking.snapshot_runs || 0 }} hourly snapshots
                </div>
                <apexchart
                  v-if="rankingChartItems.length"
                  type="bar"
                  :height="chartHeight(rankingChartItems.length)"
                  :series="rankingSeries"
                  :options="rankingOptions"
                />
                <div
                  v-else-if="rankingError"
                  class="text-danger py-4 text-center font-size-13"
                >
                  {{ rankingError }}
                </div>
                <div
                  v-else-if="!rankingLoaded || rankingBusy"
                  class="text-muted py-4 text-center"
                >
                  <b-spinner small />&nbsp;&nbsp;Loading ranking...
                </div>
                <div
                  v-else
                  class="text-muted py-4 text-center"
                >
                  Nothing matches in this window.
                </div>
              </div>
            </div>
          </div>
          <div class="col-xl-7 col-12 mb-3">
            <div class="card h-100 mb-0">
              <div class="card-body py-2">
                <div class="d-flex align-items-center mb-1 flex-wrap">
                  <h5 class="font-size-14 mb-0">
                    Ranking
                    <small
                      v-if="ranking.matched_count !== undefined"
                      class="text-muted"
                    >
                      · {{ rankingItems.length }} of {{ ranking.matched_count }}{{ q ? ` matching "${q}"` : '' }}
                    </small>
                  </h5>
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    class="ml-auto"
                    title="Export this ranking (up to 1000 rows) as CSV"
                    :disabled="exporting.length > 0 || !rankingItems.length"
                    @click="downloadCsv"
                  >
                    <feather
                      type="download"
                      class="icon-xs mr-1"
                    />
                    CSV
                  </b-button>
                </div>
                <div
                  v-if="completeness && completeness.solid < completeness.total"
                  class="text-muted font-size-12 mb-1"
                >
                  <feather
                    type="info"
                    class="icon-xs mr-1"
                  />
                  {{ completeness.solid }} of {{ completeness.total }} items were in the hourly top 25 in at least 90% of
                  the hours. For the others the total is a lower bound: in the hours they are missing they were not in that
                  hour's report (see <i>Hours in top</i>).
                </div>
                <div
                  v-if="ranking.overlap_runs"
                  class="text-warning font-size-12 mb-1"
                >
                  <feather
                    type="alert-triangle"
                    class="icon-xs mr-1"
                  />
                  In {{ ranking.overlap_runs }} hour{{ ranking.overlap_runs === 1 ? '' : 's' }} both members of
                  {{ (ranking.overlap_sites || []).join(', ') }} returned rows (failover or split brain); those hours add both reports.
                </div>
                <div class="table-responsive mb-0">
                  <b-table
                    :items="rankingItems"
                    :fields="tableFields"
                    :busy="rankingBusy && !rankingItems.length"
                    small
                    hover
                    show-empty
                    :empty-text="rankingError || 'Nothing matches in this window.'"
                    class="tops-table"
                    @row-clicked="showItem"
                  >
                    <template v-slot:table-busy>
                      <div class="text-center py-3">
                        <b-spinner
                          small
                          class="align-middle"
                        />
                        <strong>&nbsp;&nbsp;Loading...</strong>
                      </div>
                    </template>

                    <template v-slot:cell(key)="data">
                      <a
                        href="javascript:void(0)"
                        class="tops-key"
                        :title="data.item.key"
                        @click.stop="showItem(data.item)"
                      >{{ data.item.key }}</a>
                      <div
                        v-if="preset.extras.length || preset.sets.length"
                        class="mt-1"
                      >
                        <template v-for="extra in preset.extras">
                          <b-badge
                            v-if="data.item[extra.field] !== null && data.item[extra.field] !== undefined"
                            :key="extra.field"
                            :variant="extra.unit === 'risk' ? riskVariant(data.item[extra.field]) : 'light'"
                            class="mr-1"
                          >
                            {{ extra.label }} {{ data.item[extra.field] }}
                          </b-badge>
                        </template>
                        <template v-for="set in preset.sets">
                          <b-badge
                            v-for="value in data.item[set.field] || []"
                            :key="`${set.field}-${value}`"
                            variant="light"
                            class="mr-1 font-weight-normal"
                          >
                            {{ value }}
                          </b-badge>
                        </template>
                      </div>
                    </template>

                    <template v-slot:cell(share_pct)="data">
                      <div class="d-flex align-items-center justify-content-end">
                        <div class="share-bar mr-1">
                          <div
                            class="share-bar__fill"
                            :style="{ width: `${Math.min(100, data.item.share_pct)}%` }"
                          />
                        </div>
                        {{ data.item.share_pct.toFixed(1) }}%
                      </div>
                    </template>

                    <template v-slot:cell(runs_present)="data">
                      <span
                        :class="data.item.runs_present < ranking.snapshot_runs ? 'text-muted' : ''"
                        :title="`In the top 25 in ${data.item.runs_present} of ${ranking.snapshot_runs} hourly snapshots`"
                      >
                        {{ data.item.runs_present }}/{{ ranking.snapshot_runs }}
                      </span>
                    </template>
                  </b-table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Coverage -->
      <div class="card mb-3">
        <div class="card-body py-2">
          <h5 class="font-size-14 mb-1">
            <feather
              type="grid"
              class="icon-xs mr-1 text-muted"
            />
            Coverage
            <feather
              v-b-tooltip.hover
              type="help-circle"
              class="icon-xs help-tip"
              title="Snapshots that returned rows, out of those collected, per site and report kind in the window. A pair normally reads about half: the passive member answers with an empty report. Hover a cell for each member."
            />
          </h5>
          <div
            v-if="coverageError"
            class="text-danger py-3 text-center font-size-13"
          >
            Could not load this section.
          </div>
          <div
            v-else-if="!coverageLoaded"
            class="text-muted py-3 text-center"
          >
            <b-spinner small />&nbsp;&nbsp;Loading coverage...
          </div>
          <div
            v-else-if="!(coverage.rows || []).length"
            class="text-muted py-3 text-center"
          >
            No snapshots in this window.
          </div>
          <div
            v-else
            class="table-responsive"
          >
            <table class="table table-sm table-centered mb-0 font-size-13 coverage-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th
                    v-for="entry in coverageReports"
                    :key="entry.report"
                    class="text-center"
                  >
                    {{ entry.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in coverage.rows"
                  :key="row.site"
                >
                  <td class="text-nowrap">
                    <a
                      href="javascript:void(0)"
                      @click="site = row.site"
                    >{{ row.site }}</a>
                  </td>
                  <td
                    v-for="entry in coverageReports"
                    :key="entry.report"
                    class="text-center text-nowrap"
                    :class="coverageClass(row, entry.report)"
                    :title="coverageTitle(row, entry.report)"
                  >
                    <template v-if="coverageCell(row, entry.report)">
                      {{ coverageCell(row, entry.report).with_rows }}/{{ coverageCell(row, entry.report).docs }}
                    </template>
                    <template v-else>
                      —
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-if="reportsWithoutData.length"
              class="text-muted font-size-12 mt-1"
            >
              Never returned a row in the last 30 days: {{ reportsWithoutData.map((entry) => entry.label).join(', ') }}.
            </div>
          </div>
        </div>
      </div>

      <!-- Raw snapshots -->
      <div class="card mb-3">
        <div class="card-body py-2">
          <div class="d-flex align-items-center flex-wrap">
            <h5 class="font-size-14 mb-0">
              <a
                href="javascript:void(0)"
                class="text-body"
                @click="toggleSnapshots"
              >
                <feather
                  :type="snapshotsOpen ? 'chevron-down' : 'chevron-right'"
                  class="icon-xs mr-1"
                />
                Raw snapshots
              </a>
              <small class="text-muted">· the hourly documents behind the rankings</small>
            </h5>
            <b-form-checkbox
              v-if="snapshotsOpen"
              v-model="snapshotsWithRows"
              switch
              class="ml-auto font-size-13"
            >
              Only with rows
            </b-form-checkbox>
          </div>
          <template v-if="snapshotsOpen">
            <div
              v-if="snapshotsError"
              class="text-danger py-3 text-center font-size-13"
            >
              {{ snapshotsError }}
            </div>
            <div
              v-else
              class="table-responsive mt-2 mb-0"
            >
              <b-table
                :items="snapshots"
                :fields="snapshotFields"
                :busy="snapshotsBusy"
                small
                hover
                show-empty
                empty-text="No snapshots for these filters."
                @row-clicked="openSnapshot"
              >
                <template v-slot:table-busy>
                  <div class="text-center py-3">
                    <b-spinner
                      small
                      class="align-middle"
                    />
                    <strong>&nbsp;&nbsp;Loading...</strong>
                  </div>
                </template>
              </b-table>
            </div>
            <b-pagination
              v-if="snapshotsTotal > snapshotsPerPage"
              v-model="snapshotsPage"
              :total-rows="snapshotsTotal"
              :per-page="snapshotsPerPage"
              size="sm"
              class="justify-content-end mb-0"
            />
          </template>
        </div>
      </div>
    </template>

    <b-modal
      ref="modal-item"
      size="xl"
      :title="selectedItem ? `${currentReport && currentReport.label}: ${selectedItem.key}` : ''"
      :centered="true"
      :no-fade="true"
      ok-only
      @shown="onModalShown"
    >
      <TopsItemDetail
        v-if="selectedItem && preset"
        :base-url="baseURL"
        :base-path="basePath"
        :report="report"
        :preset="preset"
        :item="selectedItem"
        :site="site"
        :device="device"
        :hours="hours"
        :metric="activeMetric"
      />
    </b-modal>

    <b-modal
      ref="modal-snapshot"
      size="xl"
      :title="snapshotDoc ? `${snapshotDoc.label} · ${snapshotDoc.device}` : 'Snapshot'"
      :centered="true"
      :no-fade="true"
      ok-only
    >
      <div
        v-if="snapshotDocError"
        class="text-danger py-3 text-center font-size-13"
      >
        {{ snapshotDocError }}
      </div>
      <div
        v-else-if="!snapshotDoc"
        class="text-muted py-3 text-center"
      >
        <b-spinner small />&nbsp;&nbsp;Loading snapshot...
      </div>
      <template v-else>
        <div class="text-muted font-size-12 mb-2">
          Collected {{ formatDate(snapshotDoc.ts) }} (America/Bogota) · report window
          {{ snapshotDoc.report_start }} – {{ snapshotDoc.report_end }} on the firewall's own clock ·
          {{ snapshotDoc.ip }} · run {{ snapshotDoc.run_id }}
        </div>
        <div class="table-responsive">
          <b-table
            :items="snapshotDoc.rows || []"
            :fields="snapshotRowFields(snapshotDoc)"
            small
            show-empty
            empty-text="This snapshot returned no rows."
          />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<style scoped>
.help-tip {
  cursor: help;
  color: #ced4da;
}
.help-tip:hover {
  color: #98a6ad;
}
.tops-key {
  word-break: break-all;
}
.tops-table >>> td {
  vertical-align: middle;
}
.share-bar {
  width: 48px;
  height: 6px;
  border-radius: 3px;
  background-color: #eef2f7;
  overflow: hidden;
}
.share-bar__fill {
  height: 100%;
  background-color: #727cf5;
}
/* The theme paints every ApexCharts data label white (design/custom/plugins/
   _apexcharts.scss), which suits labels inside a bar. Here they sit past the
   end of the bar, on the card background. */
.tops-page >>> .apexcharts-datalabel {
  fill: #6c757d !important;
}
.coverage-empty {
  background-color: rgba(152, 166, 173, 0.08);
}
.coverage-table th {
  white-space: nowrap;
  font-weight: 600;
}
</style>
